/**
 * MH-37 Danh mục lĩnh vực bản tin (đợt 3 phần C4, yêu cầu nghiệp vụ mục 4), dựng
 * lại theo trang "Quản lý lĩnh vực cấp 2" của bản mẫu CMS.
 *
 * Ánh xạ bản mẫu → dữ liệu hiện có: mỗi lĩnh vực bản tin của tỉnh là "lĩnh vực
 * cấp 2"; trường `anhXaLoaiLinhVuc` (sáu mã '1'..'6' của OpenAPI) là "lĩnh vực
 * cấp 1". Backend chưa lưu thời gian tạo, người tạo và chưa có API nhập Excel.
 *
 * Sáu lĩnh vực mặc định tham gia chỉ tiêu báo cáo KHÔNG xoá được — nút xoá không
 * hiển thị cho các dòng đó, chỉ có nút "ngừng sử dụng"/"kích hoạt lại".
 */
import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { vi } from '../ngon-ngu/vi';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';
import { danhMucApi, LoiApi, type DongLinhVuc } from '../api/danh-muc-hanh-chinh-api';
import { dungTaiDuLieu } from '../trang-thai/dung-tai-du-lieu';
import { BangDuLieu, SO_DONG_MAC_DINH, type CotBang } from '../thanh-phan/BangDuLieu';
import { DaiChuaCoApi, NhanChuaCoApi, TheTag } from '../thanh-phan/MauGiaoDien';
import './quan-tri.css';

const t = viQuanTri.linhVuc;
const CAP_1 = vi.banTin.linhVucNhan;
type MaCap1 = keyof typeof CAP_1;

const tenCap1 = (ma: string): string => (ma in CAP_1 ? CAP_1[ma as MaCap1] : ma);

export function MH37LinhVucBanTin(): ReactNode {
  const [moc, datMoc] = useState(0);
  const [moTao, datMoTao] = useState(false);
  const [ma, datMa] = useState('');
  const [ten, datTen] = useState('');
  const [anhXa, datAnhXa] = useState('');
  const [dangGui, datDangGui] = useState(false);
  const [loiGui, datLoiGui] = useState<string | null>(null);
  const [thongBao, datThongBao] = useState<string | null>(null);
  const [locCap1, datLocCap1] = useState('');
  const [trang, datTrang] = useState(1);

  const kq = dungTaiDuLieu<DongLinhVuc[]>({
    tai: (signal) => danhMucApi.danhSachLinhVuc(signal),
    laRong: (d) => d.length === 0,
    dangLoc: false,
    phuThuoc: [moc],
  });
  const taiLai = (): void => datMoc((x) => x + 1);

  // API trả cả danh mục (không phân trang, không lọc) — danh mục nhỏ theo tỉnh,
  // nên lọc cấp 1 và chia trang ngay tại đây; xem nợ kỹ thuật trong báo cáo.
  const daLoc = useMemo(
    () => (kq.duLieu ?? []).filter((d) => !locCap1 || d.anhXaLoaiLinhVuc === locCap1),
    [kq.duLieu, locCap1],
  );
  const trangHienTai = daLoc.slice((trang - 1) * SO_DONG_MAC_DINH, trang * SO_DONG_MAC_DINH);
  const trangThaiBang =
    kq.trangThai === 'co-du-lieu' && daLoc.length === 0 && locCap1 ? 'rong-loc' : kq.trangThai;

  const chuyenTrangThai = async (d: DongLinhVuc): Promise<void> => {
    datThongBao(null);
    try {
      if (d.trangThai === 'hoat_dong') await danhMucApi.ngungSuDungLinhVuc(d.id);
      else await danhMucApi.kichHoatLaiLinhVuc(d.id);
      taiLai();
    } catch (err) {
      datThongBao(err instanceof LoiApi ? err.message : 'Thao tác thất bại');
    }
  };

  const xoa = async (d: DongLinhVuc): Promise<void> => {
    if (!window.confirm(`${vi.linhVucBanTin.xoa} "${d.ten}"?`)) return;
    try {
      await danhMucApi.xoaLinhVuc(d.id);
      taiLai();
    } catch (err) {
      datThongBao(err instanceof LoiApi ? err.message : 'Thao tác thất bại');
    }
  };

  const stt = new Map(trangHienTai.map((d, i) => [d.id, (trang - 1) * SO_DONG_MAC_DINH + i + 1]));

  const cot: CotBang<DongLinhVuc>[] = [
    { khoa: 'stt', nhan: t.cot.stt, ve: (d) => stt.get(d.id) ?? '' },
    {
      khoa: 'ten',
      nhan: t.cot.cap2,
      ve: (d) => (
        <>
          {d.ten} <span className="bang__dong-phu">{d.ma}</span>
        </>
      ),
    },
    { khoa: 'cap1', nhan: t.cot.cap1, ve: (d) => tenCap1(d.anhXaLoaiLinhVuc) },
    { khoa: 'thoiGianTao', nhan: t.cot.thoiGianTao, ve: () => '—' },
    { khoa: 'nguoiTao', nhan: t.cot.nguoiTao, ve: () => '—' },
    {
      khoa: 'trangThai',
      nhan: vi.linhVucBanTin.cot.trangThai,
      ve: (d) => (
        <TheTag kieu={d.trangThai === 'hoat_dong' ? 'ok' : 'trung-tinh'}>
          {vi.linhVucBanTin.trangThai[d.trangThai]}
        </TheTag>
      ),
    },
    {
      khoa: 'hanhDong',
      nhan: '',
      ve: (d) => (
        <div className="man-hinh__hanh-dong">
          <button type="button" className="nut nut--nho" onClick={() => void chuyenTrangThai(d)}>
            {d.trangThai === 'hoat_dong' ? vi.linhVucBanTin.ngungSuDung : vi.linhVucBanTin.kichHoatLai}
          </button>
          {!d.laGiaTriChuan ? (
            <button type="button" className="nut nut--nho" onClick={() => void xoa(d)}>
              {vi.linhVucBanTin.xoa}
            </button>
          ) : (
            <span className="the the--phu" title={vi.linhVucBanTin.khongXoaDuocMacDinh}>
              {vi.linhVucBanTin.cot.macDinh}
            </span>
          )}
        </div>
      ),
    },
  ];

  const guiTao = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    datDangGui(true);
    datLoiGui(null);
    try {
      await danhMucApi.themLinhVuc({ ma, ten, anhXaLoaiLinhVuc: anhXa });
      datMoTao(false);
      datMa('');
      datTen('');
      datAnhXa('');
      taiLai();
    } catch (err) {
      datLoiGui(err instanceof LoiApi ? err.message : 'Không thêm được');
    } finally {
      datDangGui(false);
    }
  };

  return (
    <div className="man-hinh man-hinh--linh-vuc-ban-tin">
      <header className="man-hinh__dau">
        <h1>{t.tieuDe}</h1>
      </header>

      <DaiChuaCoApi
        canApi={[
          'GET /api/v1/danh-muc/linh-vuc-ban-tin — trả thêm taoLuc, nguoiTao; lọc ?cap1= và phân trang page/pageSize',
          'POST /api/v1/danh-muc/linh-vuc-ban-tin/import (nhập từ Excel)',
        ]}
      />

      <div className="qt-dau">
        <select
          className="qt-select qt-select--cap-1"
          aria-label={t.locCap1}
          value={locCap1}
          onChange={(e) => {
            datLocCap1(e.target.value);
            datTrang(1);
          }}
        >
          <option value="">{t.chonCap1}</option>
          {(Object.keys(CAP_1) as MaCap1[]).map((k) => (
            <option key={k} value={k}>
              {CAP_1[k]}
            </option>
          ))}
        </select>
        <div className="qt-dau__phai">
          <button type="button" className="nut nut--chinh" onClick={() => datMoTao(true)}>
            {t.themMoi}
          </button>
          <button type="button" className="nut" disabled>
            {t.nhapExcel}
          </button>
          <NhanChuaCoApi />
        </div>
      </div>

      {thongBao ? <p role="alert">{thongBao}</p> : null}

      {moTao ? (
        <form className="hop-thoai" onSubmit={(e) => void guiTao(e)}>
          <label>
            {vi.linhVucBanTin.ma}
            <input value={ma} onChange={(e) => datMa(e.target.value)} required />
          </label>
          <label>
            {t.cot.cap2}
            <input value={ten} onChange={(e) => datTen(e.target.value)} required />
          </label>
          <label>
            {t.cap1}
            <select value={anhXa} onChange={(e) => datAnhXa(e.target.value)} required>
              <option value="">{t.chonCap1}</option>
              {(Object.keys(CAP_1) as MaCap1[]).map((k) => (
                <option key={k} value={k}>
                  {CAP_1[k]}
                </option>
              ))}
            </select>
          </label>
          {loiGui ? <p role="alert">{loiGui}</p> : null}
          <button type="submit" className="nut nut--chinh" disabled={dangGui}>
            {vi.linhVucBanTin.tao}
          </button>
          <button type="button" className="nut" onClick={() => datMoTao(false)}>
            {vi.linhVucBanTin.huy}
          </button>
        </form>
      ) : null}

      <BangDuLieu<DongLinhVuc>
        nhan={t.tieuDe}
        cot={cot}
        dong={trangHienTai}
        khoaDong={(d) => String(d.id)}
        trangThai={trangThaiBang}
        loi={kq.loi}
        taiLau={kq.taiLau}
        onThuLai={taiLai}
        tong={daLoc.length}
        trang={trang}
        soDongMoiTrang={SO_DONG_MAC_DINH}
        onDoiTrang={datTrang}
        onDoiSoDong={() => undefined}
        dangLoc={locCap1 !== ''}
        onXoaLoc={() => datLocCap1('')}
        moTaRong={vi.linhVucBanTin.rongMoTa}
      />
    </div>
  );
}
